import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type DisciplineRatingLevel,
  useDisciplineRatingLevels,
  useUpdateDisciplineRatingLevels,
} from "@/hooks/use-discipline-rating-levels";
import type { Discipline } from "@/hooks/use-disciplines";

// The Discipline Rating scale is fixed at 1–5 — it feeds the Expectation dimension of Fit
// Score against the 1–5 Expectation Level, so the scale is not growable (see
// docs/specs/discipline-rating-fit-score.md). This dialog only authors the per-level help
// text; it never changes how many levels exist.
export function DisciplineRatingLevelsDialog({
  discipline,
  open,
  onOpenChange,
}: {
  discipline: Discipline;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading } = useDisciplineRatingLevels(discipline.id, { enabled: open });
  const update = useUpdateDisciplineRatingLevels(discipline.id);
  const [draft, setDraft] = useState<DisciplineRatingLevel[]>([]);

  // Sync the editable draft from the server (a Discipline's fixed 1–5 scale) each time the
  // dialog (re)opens with data.
  useEffect(() => {
    if (open && data) {
      setDraft(data.map((l) => ({ level: l.level, helpText: l.helpText })));
    }
  }, [open, data]);

  const setHelp = (level: number, helpText: string) =>
    setDraft((prev) =>
      prev.map((r) =>
        r.level === level ? { ...r, helpText: helpText === "" ? null : helpText } : r,
      ),
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Rating levels — {discipline.code}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Describe what each rating level means for <strong>{discipline.name}</strong>. This
          guidance appears on hover when an RM rates a Resource on the Resource page, so ratings
          stay consistent. The Discipline Rating{" "}
          <strong>feeds the Expectation dimension of Fit Score</strong> — the scale is fixed at 1–5
          to match the Expectation Level.
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading levels…
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate(draft, { onSuccess: () => onOpenChange(false) });
            }}
            className="space-y-4"
          >
            <div className="space-y-3">
              {draft.map((l) => (
                <div key={l.level} className="space-y-1.5">
                  <Label htmlFor={`discipline-level-${l.level}`}>Level {l.level}</Label>
                  <Textarea
                    id={`discipline-level-${l.level}`}
                    rows={2}
                    value={l.helpText ?? ""}
                    onChange={(e) => setHelp(l.level, e.target.value)}
                    placeholder={`What does a ${l.level} mean for ${discipline.code}? (optional)`}
                    maxLength={500}
                  />
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
